// all usage of expo-router is controlled through this wrapper package (and enforced by a new eslint rule)
//
// on native:
//  - this just re-exports expo-router, and is here only for typing consistency
//  - *zero* net change in functionality
// on web:
//  - this wraps expo-router to prevent expo-router's default behavior of putting internal navigation params in URL query params
//  - this improves HIPAA compliance by preventing unnecessary PHI exposure from nav params (eg: patient UUID, first name, etc)
//  - these nav params being visible in the web URL's query params increases their exposure surface in many different ways:
//    - 3rd-party integrations (such as: (but not limited to) Mixpanel, Datadog, etc)
//    - *any* arbitrary browser extension with URL-read permission
//    - Referrer headers for any outgoing-links clicked
//    - browser profile cross-device sync
//    - screen recordings / screenshots
//    - copy/paste'd URLs
//    - browser history
//    - bookmarked URLs
// - it's also just unnecessary, not deliberate, and makes the URL UX needlessly messier
import {
  router as expoRouter,
  Redirect,
  Stack,
  Tabs,
  useFocusEffect,
  usePathname,
  useSegments,
} from "expo-router";
import type { UnknownOutputParams } from "expo-router";
import isNil from "lodash/isNil";

import {
  clearAllWebSessionRouteParams,
  readSessionParams,
  readUrlProvidedParamOverrides,
  updateSessionParams,
  wrapExpoNavMethod,
} from "@/crossPlatform/expo-router/sessionParams.web";

const router = {
  ...expoRouter,
  // dismissTo also accepts an href with params, so it must be wrapped too, or those
  // params leak into the visible URL query string (eg: dismissWithEncoding passing selectedUserId)
  dismissTo: wrapExpoNavMethod(expoRouter.dismissTo),
  navigate: wrapExpoNavMethod(expoRouter.navigate),
  push: wrapExpoNavMethod(expoRouter.push),
  // don't use expo-router's replace() on react-native-web. just don't do it
  // see: docs/web/major-impl-differences.md -> #routerreplace-bug-on-web
  //
  // in short: **each** `.replace()` invocation duplicates a full new set of the screen/tab's components
  // (+3000 elements on explore tab); memory usage and load times explode, especially on Safari
  replace: wrapExpoNavMethod(expoRouter.dismissTo),
  // since this web wrapper of expo-router keeps nav params in sessionStorage,
  // setParams needs to be overridden to instead interact with sessionStorage.
  // eslint-disable-next-line max-params
  setParams: (
    params?: Record<string, string | undefined>,
    pathname?: string,
  ) => {
    // pathname passed in explicitly rather than read via window.location,
    // as the timing/races of concurrent navigation may cause window.location reads to be wrong
    if (!isNil(params)) updateSessionParams(params, pathname);
  },
};

const useLocalSearchParams = <
  T extends UnknownOutputParams = UnknownOutputParams,
>(): T => {
  const pathname = usePathname();
  const sessionParams = readSessionParams(pathname);
  const urlParams = readUrlProvidedParamOverrides();

  // merge storage params with allowed URL-provided params (URL overrides on conflict)
  const params = { ...sessionParams, ...urlParams };

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generic return type matches expo-router's useLocalSearchParams<T> contract
  return params as T;
};

export {
  clearAllWebSessionRouteParams,
  Redirect,
  router,
  Stack,
  Tabs,
  useFocusEffect,
  useLocalSearchParams,
  usePathname,
  useSegments,
};
export type { UnknownOutputParams };
